import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import { useSystemStatus } from '../hooks/useSystemStatus';
import { useSystemMetrics } from '../hooks/useSystemMetrics';
import { useRecentActivities } from '../hooks/useRecentActivities';
import { useTaskOverview } from '../hooks/useTaskOverview';
import { useSuccessRateTrend } from '../hooks/useSuccessRateTrend';
import { useMarketplaceDistribution } from '../hooks/useMarketplaceDistribution';
import { useUserActivity } from '../hooks/useUserActivity';
import { useUserNames } from '../hooks/useUserNames';
import { useOutstandingReviewsByBrand } from '../hooks/useOutstandingReviewsByBrand';
import UserActivityChart from '../components/UserActivityChart';
import OutstandingReviewsChart from '../components/OutstandingReviewsChart';
import TestSuiteTrendChart from '../components/TestSuiteTrendChart';
import { useDefinitionTestSuiteTrend } from '../hooks/useDefinitionTestSuites';

const ChartSkeleton = ({ height = 300 }: { height?: number }) => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={height} />
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: systemStatus,
    isLoading: statusLoading,
    isFetching: statusFetching,
  } = useSystemStatus();
  const { data: systemMetrics, isFetching: metricsFetching } = useSystemMetrics();
  const {
    data: recentActivities,
    isLoading: activitiesLoading,
    isFetching: activitiesFetching,
  } = useRecentActivities(6);
  const {
    data: taskOverview,
    isLoading: taskOverviewLoading,
    isFetching: taskOverviewFetching,
  } = useTaskOverview();
  const {
    data: successRateTrend,
    isLoading: trendLoading,
    isFetching: trendFetching,
  } = useSuccessRateTrend(7);
  const {
    data: marketplaceDistribution,
    isLoading: distributionLoading,
    isFetching: distributionFetching,
  } = useMarketplaceDistribution();
  const {
    data: userActivity,
    isLoading: userActivityLoading,
    isFetching: userActivityFetching,
  } = useUserActivity(30);
  const { data: userNames = {} } = useUserNames();
  const {
    data: outstandingReviews,
    isLoading: outstandingLoading,
    isFetching: outstandingFetching,
  } = useOutstandingReviewsByBrand();
  const {
    data: testSuiteTrend,
    isLoading: testSuiteTrendLoading,
    isFetching: testSuiteTrendFetching,
    isError: testSuiteTrendError,
  } = useDefinitionTestSuiteTrend();

  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date());

  React.useEffect(() => {
    if (
      statusFetching ||
      metricsFetching ||
      activitiesFetching ||
      taskOverviewFetching ||
      trendFetching ||
      distributionFetching ||
      userActivityFetching ||
      outstandingFetching ||
      testSuiteTrendFetching
    ) {
      setLastUpdated(new Date());
    }
  }, [
    statusFetching,
    metricsFetching,
    activitiesFetching,
    taskOverviewFetching,
    trendFetching,
    distributionFetching,
    userActivityFetching,
    outstandingFetching,
    testSuiteTrendFetching,
  ]);

  const isRefreshing =
    statusFetching ||
    metricsFetching ||
    activitiesFetching ||
    taskOverviewFetching ||
    trendFetching ||
    distributionFetching ||
    userActivityFetching ||
    outstandingFetching ||
    testSuiteTrendFetching;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.default',
          py: 1,
          minHeight: 40,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 24 }}>
          <Typography variant="body2" color="textSecondary">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
          {isRefreshing && (
            <Chip
              icon={<RefreshIcon sx={{ fontSize: 16 }} />}
              label="Updating..."
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PlayIcon />}
                    fullWidth
                    onClick={() => navigate('/tasks')}
                    sx={{ mb: 1 }}
                  >
                    Create Task
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    fullWidth
                    onClick={() => navigate('/definitions/new')}
                  >
                    Add Marketplace
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Scraping Success Rate
                      </Typography>
                      {statusLoading ? (
                        <Skeleton variant="text" width="60%" height={48} />
                      ) : (
                        <Typography variant="h3" component="div" color="primary">
                          {typeof systemStatus?.average_success_rate === 'number'
                            ? `${(systemStatus.average_success_rate * 100).toFixed(1)}%`
                            : 'N/A'}
                        </Typography>
                      )}
                      {!statusLoading && (
                        <LinearProgress
                          variant="determinate"
                          value={
                            typeof systemStatus?.average_success_rate === 'number'
                              ? systemStatus.average_success_rate * 100
                              : 0
                          }
                          sx={{ mt: 1, mb: 2 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SpeedIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          {statusLoading ? (
                            <Skeleton variant="text" width={60} />
                          ) : (
                            `${systemStatus?.total_marketplaces || 0} Marketplaces`
                          )}
                        </Typography>
                      </Box>
                      {systemMetrics && (
                        <>
                          <Typography variant="body2" color="textSecondary">
                            CPU: {(systemMetrics?.cpu_usage ?? 0).toFixed(1)}%
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Memory: {(systemMetrics?.memory_usage ?? 0).toFixed(1)}%
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Task Overview
              </Typography>
              {taskOverviewLoading ? (
                <Box sx={{ mt: 2 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                  <Skeleton variant="text" width="70%" height={20} />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Active Tasks</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {taskOverview?.active_tasks || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Queued</Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.main">
                      {taskOverview?.queued_tasks || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Completed</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {taskOverview?.completed_tasks || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Failed</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      {taskOverview?.failed_tasks || 0}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <OutstandingReviewsChart
            data={outstandingReviews?.brands || []}
            loading={outstandingLoading}
            onBrandClick={brand => {
              if (brand && brand !== 'Unspecified') {
                navigate(`/reviews?brand=${encodeURIComponent(brand)}`);
              } else {
                navigate('/reviews');
              }
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {trendLoading ? (
            <ChartSkeleton height={300} />
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Success Rate Trend
                </Typography>
                {Array.isArray(successRateTrend) && successRateTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={successRateTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Line
                        type="monotone"
                        dataKey="rate"
                        stroke="#1976d2"
                        strokeWidth={2}
                        dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography color="textSecondary">No data available</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {distributionLoading ? (
            <ChartSkeleton height={300} />
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Marketplace Distribution
                </Typography>
                {Array.isArray(marketplaceDistribution) && marketplaceDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={marketplaceDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {marketplaceDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography color="textSecondary">No data available</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <TestSuiteTrendChart
            points={testSuiteTrend?.items || []}
            loading={testSuiteTrendLoading}
            error={testSuiteTrendError}
            onOpenSuite={suiteId => navigate(`/test-suites/${suiteId}`)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {userActivityLoading ? (
            <ChartSkeleton height={320} />
          ) : (
            <UserActivityChart
              title="Definition Updates by User"
              subtitle="Marketplace definition creates and config changes over the last 30 days"
              data={userActivity?.definition_updates ?? []}
              displayNames={userNames}
              height={320}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {userActivityLoading ? (
            <ChartSkeleton height={320} />
          ) : (
            <UserActivityChart
              title="Scraping Tasks by User"
              subtitle="Tasks initiated from the collector over the last 30 days"
              data={userActivity?.task_creations ?? []}
              displayNames={userNames}
              height={320}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activities
              </Typography>
              {activitiesLoading ? (
                <Box>
                  {[1, 2, 3].map(i => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="60%" height={20} />
                      <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box>
                  {recentActivities &&
                  Array.isArray(recentActivities.activities) &&
                  recentActivities.activities.length > 0 ? (
                    (recentActivities.activities as any[]).map((activity: any, index: number) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {activity.action}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {activity.time}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography color="textSecondary">No recent activities</Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
